import { toast } from "sonner";
import { confirm, choose } from "@/lib/dialog";
import { blocksApi, tasksApi } from "@/lib/api";

// Task lifecycle + drag-and-drop handlers shared between ScheduleGrid and
// FloatingTaskTray (duplicate, delete with cascade dialog, cross-room move,
// group via DnD, unparent subtasks, quick-add).
export function useTaskActions({
  project,
  data,
  refresh,
  patchTask,
  patchBlock,
  setSelection,
}) {
  const handleDuplicateTask = async (task) => {
    if (!task) return;
    try {
      await tasksApi.duplicate(task.id);
      toast.success(`Duplicated "${task.title}" — find it in FLOATING`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Duplicate failed");
    }
  };

  const handleDeleteTask = async (task) => {
    if (!task) return;
    // If the task has live subtasks, ask whether to cascade.
    const subtaskCount = data.tasks.filter(
      (t) => t.parent_task_id === task.id && !t.deleted_at
    ).length;
    let cascade = true;
    if (subtaskCount > 0) {
      const choice = await choose({
        title: `Delete "${task.title}"?`,
        body:
          `This group has ${subtaskCount} subtask${subtaskCount === 1 ? "" : "s"}.\n` +
          `Should they be deleted too, or kept as independent floating tasks?`,
        danger: true,
        options: [
          { label: `DELETE ALL ${subtaskCount + 1}`, value: "yes", danger: true },
          { label: "KEEP SUBTASKS", value: "no" },
        ],
      });
      if (choice == null) return; // user dismissed
      cascade = choice === "yes";
    } else {
      const ok = await confirm({
        title: `Delete "${task.title}"?`,
        body: "Soft-delete — recoverable from the audit log.",
        confirmLabel: "DELETE",
        danger: true,
      });
      if (!ok) return;
    }
    try {
      await tasksApi.remove(task.id, { cascade });
      if (subtaskCount > 0 && !cascade) {
        toast.success(
          `Deleted "${task.title}" · ${subtaskCount} subtask${
            subtaskCount === 1 ? "" : "s"
          } now floating`
        );
      } else {
        toast.success(`Deleted "${task.title}"`);
      }
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  // ROOMS view drag-to-room. Selection-aware: payload arrives as
  // { block_ids[], task_ids[] }. Block ids → patch room_id on the block;
  // task ids → patch primary_room_id on the (floating) task.
  const handleMoveSelectionToRoom = async (roomId, payload) => {
    const blockIds = payload?.block_ids || [];
    const taskIds = payload?.task_ids || [];
    if (!blockIds.length && !taskIds.length) return;
    // No-op detection: skip items already in the target room. Unknown ids
    // (not in local state — e.g. just-created, or stale snapshot) are
    // dispatched anyway — let the backend be the final authority.
    const blockMoves = blockIds.filter((id) => {
      const b = data.schedule_blocks.find((x) => x.id === id);
      return !b || b.room_id !== roomId;
    });
    const taskMoves = taskIds.filter((id) => {
      const t = data.tasks.find((x) => x.id === id);
      return !t || t.primary_room_id !== roomId;
    });
    if (!blockMoves.length && !taskMoves.length) {
      toast.info("Already in that room.", { id: "room-move" });
      return;
    }
    const room = data.rooms.find((r) => r.id === roomId);
    const label = room ? (room.abbreviation || room.name) : "UNASSIGNED";
    const ops = [
      ...blockMoves.map((id) => ({ kind: "block", id, p: patchBlock(id, { room_id: roomId }) })),
      ...taskMoves.map((id) => ({ kind: "task", id, p: patchTask(id, { primary_room_id: roomId }) })),
    ];
    const results = await Promise.allSettled(ops.map((t) => t.p));
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - ok;
    const total = results.length;
    if (failed === 0) {
      toast.success(total === 1 ? `Moved to ${label}` : `Moved ${total} items to ${label}`, { id: "room-move" });
    } else if (ok === 0) {
      toast.error(total === 1 ? "Move failed" : `Move failed — all ${total} items`, { id: "room-move" });
    } else {
      toast.warning(`Moved ${ok} of ${total} to ${label} · ${failed} skipped (locked or conflict)`, { id: "room-move" });
    }
  };

  // Group tasks by drag-on-drop. `payload` is the source set (task-group MIME);
  // `targetTaskId` is the row that received the drop. The backend collapses
  // child blocks into a single parent block and generates the umbrella label.
  const handleGroupTasks = async ({ payload, targetTaskId }) => {
    if (!project || !targetTaskId) return;
    // Resolve source ids → task ids (block_ids → block.task_id).
    const srcTaskIds = [];
    for (const bid of payload.block_ids || []) {
      const b = data.schedule_blocks.find((x) => x.id === bid);
      if (b?.task_id) srcTaskIds.push(b.task_id);
    }
    for (const tid of payload.task_ids || []) srcTaskIds.push(tid);
    // Dedupe but keep order; ensure target is LAST (the drop target).
    const filtered = srcTaskIds.filter((id) => id !== targetTaskId);
    if (filtered.length === 0) return; // dropped on self → no-op
    const finalIds = [...new Set([...filtered, targetTaskId])];
    if (finalIds.length < 2) return;

    const grouping = tasksApi.group(project.id, finalIds);
    toast.promise(grouping, {
      loading: `Grouping ${finalIds.length} tasks · generating AI label…`,
      success: (res) => `Grouped → "${res?.label || "Group"}"`,
      error: (e) => e?.response?.data?.detail || "Grouping failed",
    });
    try {
      await grouping;
      setSelection([]);
      await refresh();
    } catch {
      /* toast already shown */
    }
  };

  // Drag a subtask onto grid whitespace → strip it from its parent.
  // It becomes a top-level floating task again.
  const handleUnparentSubtasks = async (taskIds) => {
    const ids = (taskIds || []).filter(Boolean);
    if (!ids.length) return;
    const subs = ids
      .map((id) => data.tasks.find((t) => t.id === id))
      .filter((t) => t && t.parent_task_id && !t.deleted_at);
    if (!subs.length) return;
    try {
      await Promise.all(
        subs.map((t) => tasksApi.update(t.id, { parent_task_id: "" }))
      );
      toast.success(
        subs.length === 1
          ? `Ungrouped "${subs[0].title}"`
          : `${subs.length} tasks ungrouped`
      );
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ungroup failed");
    }
  };

  // Quick-add a floating task into a specific room (room view).
  const onQuickAddFloatingForRoom = async (roomId, title) => {
    if (!project) return;
    try {
      await tasksApi.create(project.id, {
        title,
        primary_room_id: roomId || null,
        duration_min: 60,
        is_floating: true,
        priority: "normal",
        status: "planned",
      });
      toast.success(`Added "${title}" to floating`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add task.");
    }
  };

  // Quick-add a task on a specific day — title only, defaults everything else
  // and places a block right after the last existing block on that day.
  const onQuickAddTask = async (dateKey, title) => {
    if (!project) return;
    const onday = data.schedule_blocks
      .filter((b) => (b.start_at || "").slice(0, 10) === dateKey)
      .sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
    const startAt = onday.length
      ? onday[onday.length - 1].end_at.replace(/\.\d+Z$/, "Z")
      : `${dateKey}T09:00:00Z`;
    const durationMin = 60;
    const endAt = new Date(new Date(startAt).getTime() + durationMin * 60000).toISOString().replace(/\.\d+Z$/, "Z");
    const roomId = data.rooms[0]?.id;
    if (!roomId) {
      toast.error("Add a room first.");
      return;
    }
    try {
      const t = await tasksApi.create(project.id, {
        title,
        primary_room_id: roomId,
        duration_min: durationMin,
        priority: "normal",
      });
      await blocksApi.create(project.id, {
        task_id: t.id,
        room_id: roomId,
        start_at: startAt,
        end_at: endAt,
        is_locked: false,
      });
      toast.success(`Added "${title}"`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add task.");
    }
  };

  return {
    handleDuplicateTask,
    handleDeleteTask,
    handleMoveSelectionToRoom,
    handleGroupTasks,
    handleUnparentSubtasks,
    onQuickAddFloatingForRoom,
    onQuickAddTask,
  };
}
